/**
 * Vista a calendario mensile delle partite (V16).
 *
 * La griglia comune (`CalendarioMese`) sui mesi della stagione, estesi a
 * quelli delle partite che ne cadono fuori: su telefono un pallino per
 * partita, colorato per stato; da tablet in su l'ora in una pastiglia dello
 * stesso colore, con l'avversario quando c'e spazio. Toccando un giorno se ne
 * leggono le righe, e chi gestisce crea una partita con quel giorno gia
 * compilato. Mese e giorno scelti stanno nell'URL, come la vista.
 */

import { PlusIcon } from 'lucide-react';
import { dataLocale, oraLocale } from '@calcio/shared';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CalendarioMese } from '../comuni/CalendarioMese.js';
import { type GiornoProposto, oraPiuUsata, perGiorno } from '../comuni/calendario.js';
import {
  COLORE_SEGNO,
  ETICHETTA_SEGNO,
  PALLINO_SEGNO,
  type PartitaInElenco,
  type Segno,
  segnoDi,
} from './raggruppa.js';
import { RigaPartita } from './RigaPartita.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

const LEGENDA: Segno[] = [
  'programmata',
  'formazione',
  'in_corso',
  'conclusa',
  'annullata',
  'daChiudere',
];

export function CalendarioPartite({
  ss,
  mesi,
  tutte,
  filtrate,
  mese,
  giorno,
  gestisce,
  adesso,
  onMese,
  onGiorno,
  onNuova,
}: {
  ss: string;
  /** I mesi della stagione, con quelli delle partite fuori stagione. */
  mesi: readonly string[];
  /** Tutte le partite, per l'ora da proporre. */
  tutte: readonly PartitaInElenco[];
  /** Quelle che passano i filtri: le uniche mostrate. */
  filtrate: readonly PartitaInElenco[];
  mese: string;
  giorno: string | null;
  gestisce: boolean;
  adesso: Date;
  onMese: (mese: string) => void;
  onGiorno: (giorno: string) => void;
  /** Apre la nuova partita con il giorno scelto e l'ora piu usata. */
  onNuova: (giorno: GiornoProposto) => void;
}) {
  const oggi = dataLocale(adesso);
  return (
    <CalendarioMese
      mesi={mesi}
      mese={mese}
      giorno={giorno}
      oggi={oggi}
      giorni={perGiorno(filtrate)}
      pallino={(p) => PALLINO_SEGNO[segnoDi(p, oggi)]}
      pastiglia={(p) => ({
        testo: (
          <>
            {oraLocale(p.dataOra)}
            <span className="hidden font-normal @3xl:inline"> {p.avversario}</span>
          </>
        ),
        className: cn(COLORE_SEGNO[segnoDi(p, oggi)], p.stato === 'annullata' && 'line-through'),
      })}
      legenda={LEGENDA.map((s) => ({ etichetta: ETICHETTA_SEGNO[s], colore: PALLINO_SEGNO[s] }))}
      conteggio={(n) => (n === 0 ? 'nessuna partita' : n === 1 ? '1 partita' : `${n} partite`)}
      invito="Tocca un giorno per vederne le partite."
      onMese={onMese}
      onGiorno={onGiorno}
      dettaglio={(scelto, delGiorno) => ({
        nota:
          delGiorno.some((p) => segnoDi(p, oggi) === 'daChiudere') &&
          'Programmata con la data già passata: chiudila come giocata senza timer, o annullala.',
        contenuto: (
          <>
            {delGiorno.length === 0 ? (
              <StatoVuoto titolo="Nessuna partita" />
            ) : (
              <ul className="flex flex-col gap-2">
                {delGiorno.map((p) => (
                  <li key={p.id}>
                    <RigaPartita partita={p} segno={segnoDi(p, oggi)} ss={ss} />
                  </li>
                ))}
              </ul>
            )}
            {gestisce && (
              <Button
                variant="outline"
                className="self-start"
                onClick={() => onNuova({ data: scelto, ora: oraPiuUsata(tutte, '15:00') })}
              >
                <PlusIcon data-icon="inline-start" />
                Nuova in questo giorno
              </Button>
            )}
          </>
        ),
      })}
    />
  );
}
